/**
 * R15 — the ONE snooze control: 3h / 1d / 3d / 1w presets + un-snooze. Shared by
 * the row + canvas triage clusters AND the conversation `UnifiedMuteControls`, so
 * "snooze" looks and behaves the same everywhere (snooze symmetry).
 */
export const SNOOZE_PRESETS: ReadonlyArray<{ short: string; label: string; hours: number }> = [
  { short: '3h', label: '3 hours', hours: 3 },
  { short: '1d', label: 'Tomorrow', hours: 24 },
  { short: '3d', label: '3 days', hours: 72 },
  { short: '1w', label: '1 week', hours: 168 },
];

/** ISO timestamp `hours` from now (pure-ish; reads the wall clock at call time). */
export function snoozeUntilIso(hours: number): string {
  return new Date(Date.now() + hours * 3_600_000).toISOString();
}

export function SnoozePopover({
  onPick,
  onUnsnooze,
  isSnoozed = false,
  disabled = false,
  replyAware = false,
}: {
  /** Called with the ISO `until` for a chosen preset. */
  onPick: (until: string) => void;
  /** Called to clear an active snooze (only rendered when `isSnoozed`). */
  onUnsnooze?: () => void;
  isSnoozed?: boolean;
  disabled?: boolean;
  /**
   * Thread snoozes auto-CLEAR on the next inbound message (batch-149), so every
   * thread snooze is really "remind me unless they reply first". Pass true from
   * the thread/conversation triage call sites to surface that — it makes the
   * research's "if no reply" intent honest without a redundant preset. Leave
   * false for suggestion snoozes (keep-in-touch), which are not reply-aware.
   */
  replyAware?: boolean;
}) {
  // When already snoozed, offer only un-snooze (no re-snooze presets); otherwise
  // the preset row. Keeps the existing conversation-card behaviour.
  if (isSnoozed && onUnsnooze) {
    return (
      <div className="snooze-popover" data-testid="snooze-popover">
        <button
          type="button"
          className="snooze-unsnooze"
          data-testid="snooze-clear"
          disabled={disabled}
          onClick={onUnsnooze}
        >
          Un-snooze
        </button>
      </div>
    );
  }
  return (
    <div className="snooze-popover" data-testid="snooze-popover">
      <span className="snooze-popover-label">Snooze for</span>
      <div className="snooze-popover-row">
        {SNOOZE_PRESETS.map((p) => (
          <button
            key={p.short}
            type="button"
            className="snooze-preset"
            data-testid={`snooze-${p.short}`}
            disabled={disabled}
            title={p.label}
            onClick={() => onPick(snoozeUntilIso(p.hours))}
          >
            {p.short}
          </button>
        ))}
      </div>
      {replyAware ? (
        <span className="snooze-popover-note" data-testid="snooze-reply-aware">
          Returns sooner if they reply
        </span>
      ) : null}
    </div>
  );
}
